import { fetchConfig } from '@theme/utilities';
import { CartLinesUpdateEvent, CartErrorEvent } from '@shopify/events';

/**
 * Quick add for Anthos product cards and the scent-line picker.
 * Mirrors Horizon's product-form flow (CartLinesUpdateEvent + section refresh) so the
 * cart drawer and cart bubble update exactly as they do for the main product form.
 */
async function addLines(source, items) {
  const sectionIds = [...document.querySelectorAll('cart-items-component')]
    .map((el) => el instanceof HTMLElement && el.dataset.sectionId)
    .filter(Boolean);

  const deferred = CartLinesUpdateEvent.createPromise();
  source.dispatchEvent(
    new CartLinesUpdateEvent({
      action: 'add',
      context: 'product',
      lines: items.map((i) => ({ merchandiseId: String(i.id), quantity: i.quantity })),
      promise: deferred.promise,
    })
  );

  const cfg = fetchConfig('json', { body: JSON.stringify({ items, sections: sectionIds.join(',') }) });
  const response = await fetch(Theme.routes.cart_add_url, cfg).then((r) => r.json());
  const ajaxCart = await fetch(`${Theme.routes.cart_url}.json`).then((r) => r.json());

  if (response.status) {
    source.dispatchEvent(new CartErrorEvent({ error: response.message || 'Add to cart failed', code: 'INVALID' }));
    deferred.resolve({ cart: CartLinesUpdateEvent.createCartFromAjaxResponse(ajaxCart), detail: { didError: true, items: ajaxCart.items } });
    throw new Error(response.description || response.message);
  }

  deferred.resolve({
    cart: CartLinesUpdateEvent.createCartFromAjaxResponse(ajaxCart),
    detail: { didError: false, items: ajaxCart.items, sections: response.sections, source: 'anthos-quick-add' },
  });
  return ajaxCart;
}

class AnthosQuickAdd extends HTMLElement {
  connectedCallback() {
    this.form = this.querySelector('form');
    // the price lives in the card header, outside this element
    this.price = (this.closest('.anthos-card') || this.closest('product-card') || this).querySelector('[data-anthos-price]');
    // collection cards are one big link: keep taps between the pills from opening the product
    this.addEventListener('click', this.onClick);
    this.form?.addEventListener('change', this.onChange);
    this.form?.addEventListener('submit', this.onSubmit);
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.onClick);
    this.form?.removeEventListener('change', this.onChange);
    this.form?.removeEventListener('submit', this.onSubmit);
  }

  onClick = (event) => {
    if (!event.target.closest('a')) event.stopPropagation();
  };

  onChange = (event) => {
    const input = event.target;
    if (input?.name === 'id' && this.price && input.dataset.price) this.price.textContent = input.dataset.price;
  };

  onSubmit = async (event) => {
    event.preventDefault();
    const button = this.form.querySelector('[type="submit"]');
    const id = new FormData(this.form).get('id');
    if (!id) return;
    button?.setAttribute('aria-busy', 'true');
    button?.setAttribute('disabled', '');
    try {
      await addLines(this, [{ id: Number(id), quantity: 1 }]);
      this.dataset.added = 'true';
      setTimeout(() => delete this.dataset.added, 2000);
    } catch (error) {
      console.warn('[anthos-quick-add]', error);
    } finally {
      button?.removeAttribute('aria-busy');
      button?.removeAttribute('disabled');
    }
  };
}

/** "Complete the scent line": tick products, see the running total, add all at once. */
class AnthosLinePicker extends HTMLElement {
  connectedCallback() {
    this.summary = this.querySelector('[data-line-summary]');
    this.button = this.querySelector('[data-line-add]');
    this.addEventListener('change', this.update);
    this.button?.addEventListener('click', this.addSelected);
    if (this.dataset.src) this.load();
    else this.update();
  }

  /** Pull the rest of the scent line (same `line-…` tag) and drop the product being viewed. */
  async load() {
    const target = this.querySelector('[data-line-items]');
    try {
      const html = await fetch(this.dataset.src).then((r) => r.text());
      const source = new DOMParser().parseFromString(html, 'text/html').querySelector('[data-line-items-source]');
      source?.querySelector(`[data-handle="${CSS.escape(this.dataset.current || '')}"]`)?.remove();
      const items = source ? [...source.children] : [];
      if (!items.length) {
        this.closest('section')?.setAttribute('hidden', '');
        return;
      }
      target.replaceChildren(...items);
    } catch (error) {
      console.warn('[anthos-line-picker]', error);
      this.closest('section')?.setAttribute('hidden', '');
    }
    this.update();
  }

  get selected() {
    return [...this.querySelectorAll('input[type="checkbox"][data-variant]:checked')];
  }

  update = () => {
    const picked = this.selected;
    const cents = picked.reduce((sum, el) => sum + Number(el.dataset.cents || 0), 0);
    const money = new Intl.NumberFormat(document.documentElement.lang || 'el', { style: 'currency', currency: this.dataset.currency || 'EUR' }).format(cents / 100);
    if (this.summary) this.summary.textContent = (this.dataset.summaryTemplate || '[count] · [total]').replace('[count]', picked.length).replace('[total]', money);
    if (this.button) this.button.disabled = picked.length === 0;
  };

  addSelected = async () => {
    const items = this.selected.map((el) => ({ id: Number(el.dataset.variant), quantity: 1 }));
    if (!items.length) return;
    this.button.setAttribute('aria-busy', 'true');
    try {
      await addLines(this, items);
    } catch (error) {
      console.warn('[anthos-line-picker]', error);
    } finally {
      this.button.removeAttribute('aria-busy');
    }
  };
}

if (!customElements.get('anthos-quick-add')) customElements.define('anthos-quick-add', AnthosQuickAdd);
if (!customElements.get('anthos-line-picker')) customElements.define('anthos-line-picker', AnthosLinePicker);

/** Cart drawer suggestions: loads the anthos-cart-upsell section for the src (scent line or best sellers).
 *  Fetched fresh each time: the section skips products already in the cart, and the drawer re-renders after every change. */
class AnthosCartUpsell extends HTMLElement {
  static observedAttributes = ['data-src'];

  connectedCallback() {
    this.load();
    // Horizon morphs the drawer in place after cart changes, which empties the list without
    // reconnecting this element; reload whenever that happens.
    this.observer = new MutationObserver(() => {
      const list = this.querySelector('[data-upsell-list]');
      if (list && !list.children.length && !this.loading) this.load();
    });
    this.observer.observe(this, { childList: true, subtree: true });
  }

  disconnectedCallback() {
    this.observer?.disconnect();
  }

  attributeChangedCallback(name, oldValue, newValue) {
    if (oldValue && oldValue !== newValue && this.isConnected) this.load();
  }

  async load() {
    const list = this.querySelector('[data-upsell-list]');
    const src = this.dataset.src;
    if (!list || !src) return;
    this.loading = true;
    const url = new URL(src, location.origin);
    url.searchParams.set('section_id', 'anthos-cart-upsell');
    try {
      const html = await (await fetch(url)).text();
      const rows = new DOMParser().parseFromString(html, 'text/html').querySelector('.anthos-upsell__rows');
      if (rows && rows.children.length) {
        list.replaceChildren(...rows.children);
        this.setAttribute('data-ready', '');
      } else {
        this.removeAttribute('data-ready');
      }
    } catch (e) {
      this.removeAttribute('data-ready');
    } finally {
      this.loading = false;
    }
  }
}
if (!customElements.get('anthos-cart-upsell')) customElements.define('anthos-cart-upsell', AnthosCartUpsell);

/* Product page opens on 100ml (George: "default selected size 100ml"), matching the cards.
   Shopify always renders the first variant (30ml) when the URL has no ?variant=, so on load
   we pick the 100ml pill once; links that already name a variant are left alone. */
(() => {
  if (new URLSearchParams(location.search).has('variant')) return;
  const run = () => {
    const picker = document.querySelector('main variant-picker');
    if (!picker) return;
    const target = [...picker.querySelectorAll('input[type="radio"]')]
      .find((i) => /^100\s*ml$/i.test(i.value.trim()) && i.dataset.optionAvailable !== 'false');
    if (target && !target.checked) target.click();
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', run);
  else run();
})();

/* Collection intro is clamped to 2 lines on phones (anthos.css); tap toggles the full text. */
document.addEventListener('click', (e) => {
  const intro = e.target.closest('[class*="__text_twGGkJ"]');
  if (intro && matchMedia('(max-width: 749px)').matches) intro.classList.toggle('is-open');
});

/* First-visit welcome offer (snippets/anthos-welcome.liquid). Opens after 12s or half a page of
   scrolling; a dismiss or sign-up hides it for 30 days. After the form posts, the page reloads with
   the code panel, which opens straight away. Storage can be unavailable, so every access is guarded. */
class AnthosWelcome extends HTMLElement {
  static KEY = 'anthos:welcome-hidden-until';
  connectedCallback() {
    this.addEventListener('click', (e) => {
      if (e.target === this || e.target.closest('[data-welcome-close]')) this.close();
    });
    this.addEventListener('keydown', (e) => { if (e.key === 'Escape') this.close(); });
    if (this.querySelector('[data-welcome-done], [data-welcome-error]')) return this.open(true);
    if (this.#hiddenUntil() > Date.now()) return;
    const show = () => {
      cleanup();
      // Wait for the answer to the cookie banner instead of opening on top of it.
      if (document.getElementById('shopify-pc__banner')?.getClientRects().length) return setTimeout(show, 2000);
      this.open(false);
    };
    const onScroll = () => { if (scrollY > document.documentElement.scrollHeight * 0.5 - innerHeight) show(); };
    const timer = setTimeout(show, 12000);
    const cleanup = () => { clearTimeout(timer); removeEventListener('scroll', onScroll); };
    addEventListener('scroll', onScroll, { passive: true });
  }
  open(final) {
    this.hidden = false;
    requestAnimationFrame(() => this.classList.add('is-open'));
    if (final) this.#hide(30);
    this.querySelector('input[type="email"], button')?.focus({ preventScroll: true });
  }
  close() { this.classList.remove('is-open'); this.hidden = true; this.#hide(30); }
  #hide(days) { try { localStorage.setItem(AnthosWelcome.KEY, String(Date.now() + days * 864e5)); } catch (e) {} }
  #hiddenUntil() { try { return Number(localStorage.getItem(AnthosWelcome.KEY)) || 0; } catch (e) { return 0; } }
}
if (!customElements.get('anthos-welcome')) customElements.define('anthos-welcome', AnthosWelcome);

/* Cart gift wrap (snippets/anthos-cart-extras.liquid, part 'gift'): the checkbox adds or removes the
   gift-wrap product through the same CartLinesUpdateEvent flow as quick add, so the drawer and the
   cart page re-render; the message is saved as the cart attribute "Μήνυμα δώρου" (shown on the order). */
async function setLineQuantity(source, variantId, quantity) {
  const sectionIds = [...document.querySelectorAll('cart-items-component')]
    .map((el) => el instanceof HTMLElement && el.dataset.sectionId)
    .filter(Boolean);
  const deferred = CartLinesUpdateEvent.createPromise();
  source.dispatchEvent(new CartLinesUpdateEvent({
    action: quantity > 0 ? 'add' : 'remove', context: 'cart',
    lines: [{ merchandiseId: String(variantId), quantity }], promise: deferred.promise,
  }));
  const cfg = fetchConfig('json', { body: JSON.stringify({ updates: { [variantId]: quantity }, sections: sectionIds.join(',') }) });
  const response = await fetch(Theme.routes.cart_update_url, cfg).then((r) => r.json());
  const ajaxCart = await fetch(`${Theme.routes.cart_url}.json`).then((r) => r.json());
  deferred.resolve({
    cart: CartLinesUpdateEvent.createCartFromAjaxResponse(ajaxCart),
    detail: { didError: !!response.status, items: ajaxCart.items, sections: response.sections, source: 'anthos-gift' },
  });
}

class AnthosGift extends HTMLElement {
  connectedCallback() {
    this.toggle = this.querySelector('[data-gift-toggle]');
    this.message = this.querySelector('[data-gift-message]');
    this.toggle?.addEventListener('change', this.onToggle);
    this.message?.addEventListener('input', this.onMessage);
  }

  disconnectedCallback() {
    this.toggle?.removeEventListener('change', this.onToggle);
    this.message?.removeEventListener('input', this.onMessage);
  }

  onToggle = async () => {
    const on = this.toggle.checked;
    this.toggle.disabled = true;
    try {
      await setLineQuantity(this, this.dataset.variant, on ? 1 : 0);
      if (!on && this.message) {
        this.message.value = '';
        this.#saveMessage('');
      }
    } finally {
      this.toggle.disabled = false;
    }
  };

  onMessage = () => {
    clearTimeout(this.timer);
    this.timer = setTimeout(() => this.#saveMessage(this.message.value.trim()), 600);
  };

  #saveMessage(value) {
    fetch(Theme.routes.cart_update_url, fetchConfig('json', { body: JSON.stringify({ attributes: { 'Μήνυμα δώρου': value } }) }));
  }
}
if (!customElements.get('anthos-gift')) customElements.define('anthos-gift', AnthosGift);

/* Gift set on perfume pages (blocks/anthos-gift-set.liquid): adds the scent's bundle, plus gift wrap when ticked
   and not already in the cart. */
class AnthosGiftSet extends HTMLElement {
  connectedCallback() {
    this.button = this.querySelector('[data-giftset-add]');
    this.wrap = this.querySelector('[data-giftset-wrap]');
    this.button?.addEventListener('click', this.onAdd);
  }

  disconnectedCallback() {
    this.button?.removeEventListener('click', this.onAdd);
  }

  onAdd = async () => {
    const items = [{ id: Number(this.dataset.set), quantity: 1 }];
    if (this.wrap?.checked && this.dataset.wrap) {
      const cart = await fetch(`${Theme.routes.cart_url}.json`).then((r) => r.json());
      if (!cart.items.some((i) => String(i.variant_id) === this.dataset.wrap)) items.push({ id: Number(this.dataset.wrap), quantity: 1 });
    }
    this.button.setAttribute('aria-busy', 'true');
    this.button.setAttribute('disabled', '');
    try {
      await addLines(this, items);
      this.dataset.added = 'true';
      setTimeout(() => delete this.dataset.added, 2000);
    } catch (error) {
      console.warn('[anthos-gift-set]', error);
    } finally {
      this.button.removeAttribute('aria-busy');
      this.button.removeAttribute('disabled');
    }
  };
}
if (!customElements.get('anthos-gift-set')) customElements.define('anthos-gift-set', AnthosGiftSet);
